import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { ICurrency } from '@containers/App/model';
import { VoucherDiscountMaster } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount';
import { IVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/VoucherDiscount';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { IImageProperties } from '@helpers/utils';

import { MainImage } from './MainImage';
import { messages } from './messages';
import { StickyBar } from './StickyBar';
import {
  Annotation,
  BuyLink,
  Description,
  DescriptionWrapper,
  H1,
  TopSection,
  VoucherWrapper,
} from './styled';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

interface IHeaderProps {
  annotation: string;
  productFullName: string;
  productName: string;
  redirectUrl: string;
  simpleDescription: string | null;
  selectedVariant: Product['variants'][number];
  imageAttrs: IImageProperties;
  currency: ICurrency;
  hasRichContent: boolean;
}

export const Header: React.FC<IHeaderProps> = ({
  annotation,
  productFullName,
  imageAttrs,
  redirectUrl,
  simpleDescription,
  selectedVariant,
  currency,
  hasRichContent,
}) => {
  const [ref, inView] = useInView({ initialInView: true });
  const { getTimeFromInit } = useTrackingContext();

  const handleBuyLinkClicked = () => {
    dispatchTrackingEvent({
      event: 'element_click',
      element: {
        action: 'open_pdp',
        type: 'review',
        timing: getTimeFromInit(),
        name: 'open_pdp',
        interaction: inView ? 'button' : 'scrolled_button',
      },
      _clear: true,
    });
  };

  const voucherDiscount: IVoucherDiscount = selectedVariant.attributes
    .VoucherDiscount
    ? {
        ...selectedVariant.attributes.VoucherDiscount,
        displayType: 'Percentage',
      }
    : null;

  return (
    <TopSection ref={ref}>
      <MainImage imageAttrs={imageAttrs} />

      <DescriptionWrapper>
        <H1>
          <FormattedMessage
            {...messages.reviewsPageTitle}
            values={{ productFullName }}
          />
        </H1>

        <Annotation>{annotation}</Annotation>

        {simpleDescription && (
          <Description
            dangerouslySetInnerHTML={{ __html: simpleDescription }}
          />
        )}

        {voucherDiscount && (
          <VoucherWrapper>
            <VoucherDiscountMaster
              currency={currency}
              voucherDiscount={voucherDiscount}
              hideLine={true}
              isArrowHidden={true}
            />
          </VoucherWrapper>
        )}

        <BuyLink onClick={handleBuyLinkClicked} href={redirectUrl}>
          <FormattedMessage {...messages.showProductDetail} />
        </BuyLink>
      </DescriptionWrapper>

      {simpleDescription && (
        <Description dangerouslySetInnerHTML={{ __html: simpleDescription }} />
      )}

      <BuyLink
        onClick={handleBuyLinkClicked}
        fullWidth={true}
        href={redirectUrl}
      >
        <FormattedMessage {...messages.showProductDetail} />
      </BuyLink>

      {!inView && (
        <StickyBar
          annotation={annotation}
          redirectUrl={redirectUrl}
          productName={productFullName}
          handleBuyLinkClicked={handleBuyLinkClicked}
          hasRichContent={hasRichContent}
        />
      )}
    </TopSection>
  );
};
